import { numFont } from '../../../utils/fonts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import DecimalField from '../../shared/DecimalField';
import { formatPrice, calcPriceInclVat, DEFAULT_VAT_RATE } from '../../../utils/vat';
import { CARD_SX, COMPACT_INPUT_SX, CodeText, ExVatPrice, Overline, TONE, TagChip, VendorHeading, destinationText } from './builderUi';
import { DefaultDestinationBar, LineDestinationSelect } from './DestinationControls';
import { cartGroups, cartTotals, destinationSummary, lineProblem } from './cartUtils';

function CartLineRow({ line, context, onUpdate, onRemove, newLahete, onNewLaheteChange, canViewPrices, problem, t, serverError }) {
  const total = (parseFloat(line.purchasePrice) || 0) * (parseFloat(line.quantity) || 0);
  const flagged = !!(problem || serverError);
  return (
    <Box
      data-testid={`cart-line-${line.key}`}
      sx={{
        py: 1.5,
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        '& + &': { borderTop: '1px solid', borderColor: 'divider' },
        bgcolor: flagged ? TONE.amber.bgcolor : 'transparent',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }} noWrap title={line.name}>
            {line.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 0.25 }}>
            <CodeText>{line.code}</CodeText>
            {line.brand && <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{line.brand}</Typography>}
            {line.freeText && <TagChip label={t('orderBuilder.chip.freeText')} tone={TONE.slate} sx={{ height: 20, fontSize: 11 }} />}
          </Box>
        </Box>
        <IconButton size="small" onClick={onRemove} aria-label={t('Remove')} data-testid={`cart-remove-${line.key}`} sx={{ color: 'text.secondary', mt: -0.5, mr: -0.75 }}>
          <DeleteOutlineIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <DecimalField
          size="small"
          value={line.quantity}
          onChange={(v) => onUpdate({ quantity: v })}
          sx={{ width: 92, ...COMPACT_INPUT_SX, '& input': { textAlign: 'right', fontWeight: 600, ...numFont } }}
          inputProps={{ 'data-testid': `cart-qty-${line.key}`, 'aria-label': t('orderBuilder.quantity') }}
          InputProps={{ endAdornment: <Typography sx={{ fontSize: 11.5, color: 'text.secondary', ml: 0.5 }}>{line.unit || 'kpl'}</Typography> }}
        />
        {canViewPrices && (
          <>
            <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>×</Typography>
            {line.priceEditable ? (
              <DecimalField
                size="small"
                value={line.purchasePrice ?? ''}
                onChange={(v) => onUpdate({ purchasePrice: v === '' ? null : v })}
                sx={{ width: 128, ...COMPACT_INPUT_SX, '& input': { textAlign: 'right', fontWeight: 600, ...numFont } }}
                inputProps={{ 'data-testid': `cart-price-${line.key}`, 'aria-label': t('orderBuilder.unitPrice') }}
                InputProps={{
                  endAdornment: (
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', ml: 0.5, whiteSpace: 'nowrap' }}>€ {t('orderBuilder.exVatTag')}</Typography>
                  ),
                }}
              />
            ) : (
              <ExVatPrice value={line.purchasePrice} t={t} size={13} weight={500} testId={`cart-price-${line.key}`} />
            )}
            <Box sx={{ ml: 'auto' }}>
              <ExVatPrice value={line.purchasePrice == null ? null : total} t={t} size={14} weight={700} testId={`cart-total-${line.key}`} />
            </Box>
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <LineDestinationSelect
          context={context}
          line={line}
          onChange={(destination) => onUpdate({ destination })}
          newLahete={newLahete}
          onNewLaheteChange={onNewLaheteChange}
        />
        {problem && (
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: TONE.amber.color, display: 'inline-flex', alignItems: 'center', gap: 0.5 }} data-testid={`cart-problem-${line.key}`}>
            <WarningAmberOutlinedIcon sx={{ fontSize: 15 }} />
            {t(`orderBuilder.problem.${problem}`)}
          </Typography>
        )}
        {serverError && (
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: TONE.red.color }} data-testid={`cart-server-error-${line.key}`}>
            {serverError}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * The cart: default destination for new rows, lines grouped per vendor (one order each),
 * per-line destination, totals with both VAT lines, and the way to the review dialog.
 */
function OrderCart({
  context,
  lines,
  onUpdateLine,
  onRemoveLine,
  onClear,
  defaultDestination,
  onDefaultDestinationChange,
  newLahete,
  onNewLaheteChange,
  onContinue,
  canViewPrices,
  lineErrors = {},
  dense = false,
}) {
  const { t } = useTranslation();
  const groups = cartGroups(lines, context);
  const totals = cartTotals(lines);
  const problems = {};
  lines.forEach((line) => {
    const problem = lineProblem(line, { newLahete });
    if (problem) problems[line.key] = problem;
  });
  const problemCount = Object.keys(problems).length;
  const summary = destinationSummary(lines);
  const pad = { xs: 1.5, md: 2 };

  return (
    <Box data-testid="order-cart" sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
      <Box sx={{ px: pad, pt: 1.5, pb: 1.75 }}>
        <DefaultDestinationBar
          context={context}
          value={defaultDestination}
          onChange={onDefaultDestinationChange}
          newLahete={newLahete}
          onNewLaheteChange={onNewLaheteChange}
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: dense ? 'visible' : 'auto', minHeight: 0, px: pad, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {lines.length === 0 ? (
          <Box sx={{ py: 4, px: 2, textAlign: 'center' }} data-testid="cart-empty">
            <Box
              sx={{
                width: 52, height: 52, borderRadius: '14px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', mb: 1.25,
              }}
            >
              <ShoppingCartOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary', lineHeight: 1.45 }}>{t('orderBuilder.cartEmpty')}</Typography>
          </Box>
        ) : (
          groups.map((group) => (
            <Box key={group.integration?.id || 'none'} sx={CARD_SX} data-testid={`cart-group-${group.integration?.id}`}>
              <VendorHeading
                integration={group.integration}
                t={t}
                sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
                aside={
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap', ...numFont }}>
                    {t('orderBuilder.rowCount', { count: group.lines.length })}
                    {canViewPrices && (
                      <>
                        {' · '}
                        <ExVatPrice value={group.totals.hasUnknownPrice ? null : group.totals.total} t={t} size={12.5} weight={600} muted />
                      </>
                    )}
                  </Typography>
                }
              />
              {group.lines.map((line) => (
                <CartLineRow
                  key={line.key}
                  line={line}
                  context={context}
                  onUpdate={(patch) => onUpdateLine(line.key, patch)}
                  onRemove={() => onRemoveLine(line.key)}
                  newLahete={newLahete}
                  onNewLaheteChange={onNewLaheteChange}
                  canViewPrices={canViewPrices}
                  problem={problems[line.key]}
                  serverError={lineErrors[line.key]}
                  t={t}
                />
              ))}
            </Box>
          ))
        )}
      </Box>
      {lines.length > 0 && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', px: pad, pt: 1.75, pb: 2 }} data-testid="cart-footer">
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
            {summary.map((entry, i) => (
              <TagChip
                key={i}
                tone={TONE.slate}
                label={`${destinationText(t, entry.destination, { newLahete, inventoryEnabled: context?.inventory_tracking_enabled })} · ${entry.count}`}
              />
            ))}
          </Box>
          {groups.length > 1 && (
            <Alert severity="info" sx={{ mb: 1.5, py: 0.25, borderRadius: '10px', '& .MuiAlert-message': { fontSize: 12.5 } }} data-testid="cart-split-notice">
              {t('orderBuilder.splitNotice', { count: groups.length })}
            </Alert>
          )}
          {canViewPrices && (
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Box>
                <Overline>{t('orderBuilder.totalExVat')}</Overline>
                <Typography
                  sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.1, whiteSpace: 'nowrap', ...numFont }}
                  data-testid="cart-total-ex-vat"
                >
                  {totals.hasUnknownPrice ? t('orderBuilder.totalPartial', { amount: formatPrice(totals.total) }) : `${formatPrice(totals.total)} €`}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', pb: '3px', ...numFont }} data-testid="cart-total-incl-vat">
                {t('orderBuilder.totalInclVat', { rate: '25,5' })} {formatPrice(calcPriceInclVat(totals.total, DEFAULT_VAT_RATE))} €
              </Typography>
            </Box>
          )}
          {problemCount > 0 && (
            <Typography
              sx={{ fontSize: 12.5, fontWeight: 500, color: TONE.amber.color, mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
              data-testid="cart-problems"
            >
              <WarningAmberOutlinedIcon sx={{ fontSize: 15 }} />
              {t('orderBuilder.problemsBlock', { count: problemCount })}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.75, alignItems: 'center' }}>
            <Button size="small" onClick={onClear} sx={{ textTransform: 'none', color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }} data-testid="cart-clear">
              {t('orderBuilder.clearCart')}
            </Button>
            <Button
              variant="contained"
              fullWidth
              disabled={problemCount > 0}
              onClick={onContinue}
              sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', py: 1 }}
              data-testid="cart-continue"
            >
              {t('orderBuilder.continue')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default OrderCart;
